import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Gift, Sparkles, Check, ArrowRight, AlertCircle } from "lucide-react";
import { fetchProducts, ShopifyProduct, formatPrice, DISCOUNT_PERCENTAGE } from "@/lib/shopify";
import { AnimatedSection } from "@/components/animations/AnimatedSection";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Bundle configuration - EXCLUSIVE discounts (not stackable with NY25)
const bundles = [
  {
    id: "duo",
    name: "Duo Pack",
    description: "2 accessories of your choice",
    discount: 30, // 30% on the pack (better than 25% NY25)
    badge: "Most Popular",
    popular: true,
    benefits: ["Free shipping", "30% off bundle"],
    itemCount: 2,
  },
  {
    id: "trio",
    name: "Trio Pack",
    description: "3 accessories of your choice",
    discount: 35, // 35% on the pack (better than 25% + 10%)
    badge: "Best Value",
    popular: false,
    benefits: ["Free shipping", "35% off bundle"],
    itemCount: 3,
  },
];

export const BundleSection = () => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const fetchedProducts = await fetchProducts(6);
        setProducts(fetchedProducts);
      } catch (error) {
        console.error("Error fetching products for bundles:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadProducts();
  }, []);

  // Calculate bundle prices based on real products (using ORIGINAL prices, not discounted)
  const getAverageOriginalPrice = () => {
    if (products.length === 0) return 49.9;
    const prices = products.map((p) => parseFloat(p.node.priceRange.minVariantPrice.amount));
    return prices.reduce((a, b) => a + b, 0) / prices.length;
  };

  const avgOriginalPrice = getAverageOriginalPrice();

  return (
    <AnimatedSection className="section-padding bg-gradient-to-b from-secondary/30 to-background">
      <div className="container-calim">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="text-center mb-10 md:mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Gift className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">Exclusive Offers</span>
          </div>
          <h2 className="text-headline mb-4">Save with Our Bundles</h2>
          <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
            Combine multiple accessories and enjoy exceptional discounts. Perfect as a gift or to complete your
            collection.
          </p>
        </motion.div>

        {/* Important notice about discount stacking */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="max-w-2xl mx-auto mb-8"
        >
          <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-800 dark:text-amber-200">Bundle Discounts = Best Deals</p>
              <p className="text-amber-700 dark:text-amber-300 mt-1">
              Bundle discounts (-30% and -35%) are <strong>more advantageous</strong> than the CS25 code (-
                {DISCOUNT_PERCENTAGE}%) or the welcome code. These offers cannot be combined.
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
          {bundles.map((bundle, index) => {
            // Use ORIGINAL price (before any discounts)
            const originalTotal = avgOriginalPrice * bundle.itemCount;
            // Apply pack discount (NOT cumulative with other discounts)
            const packDiscountedTotal = originalTotal * (1 - bundle.discount / 100);
            // Compare with NY25 discount for transparency
            const ny25DiscountedTotal = originalTotal * (1 - DISCOUNT_PERCENTAGE / 100);
            const packSavings = originalTotal - packDiscountedTotal;
            const extraSavingsVsNY25 = ny25DiscountedTotal - packDiscountedTotal;

            return (
              <motion.div
                key={bundle.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-background rounded-2xl border-2 p-5 md:p-6 transition-all hover:shadow-lg ${
                  bundle.popular ? "border-primary shadow-md" : "border-border hover:border-primary/50"
                }`}
              >
                {/* Badge */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span
                    className={`px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                      bundle.popular ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"
                    }`}
                  >
                    {bundle.badge}
                  </span>
                </div>

                {/* Header */}
                <div className="text-center pt-4 pb-4 border-b border-border mb-4">
                  <h3 className="text-xl md:text-2xl font-bold mb-1">{bundle.name}</h3>
                  <p className="text-muted-foreground text-sm">{bundle.description}</p>
                </div>

                {/* Product thumbnails */}
                {products.length > 0 && (
                  <div className="flex justify-center gap-2 mb-4">
                    {products.slice(0, bundle.itemCount).map((product, i) => (
                      <div
                        key={i}
                        className="w-12 md:w-14 h-12 md:h-14 rounded-lg overflow-hidden border border-border bg-secondary/30"
                      >
                        <img
                          src={product.node.images.edges[0]?.node.url}
                          alt={product.node.title}
                          loading="lazy"
                          decoding="async"
                          width={56}
                          height={56}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                    <div className="w-12 md:w-14 h-12 md:h-14 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground text-xs">
                      +...
                    </div>
                  </div>
                )}

                {/* Pricing */}
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
                    <span className="text-lg md:text-2xl text-muted-foreground line-through">
                      {formatPrice(originalTotal.toFixed(2), "EUR")}
                    </span>
                    <span className="text-2xl md:text-4xl font-bold text-primary">
                      {formatPrice(packDiscountedTotal.toFixed(2), "EUR")}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-col gap-1">
                    <div className="inline-flex items-center justify-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                      <Sparkles className="h-3 w-3" />
                      Save {formatPrice(packSavings.toFixed(2), "EUR")}
                    </div>
                    {extraSavingsVsNY25 > 0 && (
                      <span className="text-xs text-muted-foreground">
                        +{formatPrice(extraSavingsVsNY25.toFixed(2), "EUR")} more than with CS25
                      </span>
                    )}
                  </div>
                </div>

                {/* Benefits */}
                <ul className="space-y-2 mb-6">
                  {bundle.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link
                  to="/#products"
                  onClick={(e) => {
                    e.preventDefault();
                    // Scroll to products section
                    const productsSection = document.getElementById("products");
                    if (productsSection) {
                      productsSection.scrollIntoView({ behavior: "smooth" });
                    } else {
                      window.location.href = "/#products";
                    }
                  }}
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-all cursor-pointer text-sm md:text-base ${
                    bundle.popular
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-secondary text-foreground hover:bg-secondary/80"
                  }`}
                >
                  Build My Bundle
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Additional info */}
        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="text-center text-sm text-muted-foreground mt-8 max-w-xl mx-auto"
        >
          💡 <strong>Tip:</strong> Bundles are automatically the best deals. No promo code needed — the discount is
          applied automatically.
        </motion.p>
      </div>
    </AnimatedSection>
  );
};
